import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatAgeRange, formatGeographicRestriction } from '../../../hooks/utils/formatProgramInfo';
import ProgramInfoItem from './ProgramInfoItem';
import programPropsShape, { defaultProgramProps } from '../programPropsShape';
import messages, {
  accOptionsMessages,
  deliveryOptMessages,
  languageOptMessages,
  populationOptMessages,
  paymentOptMessages,
  transportationOptMessages,
  translateOptions,
} from './messages';
import useFilteredPopulations from '../../../hooks/useFilteredPopulations';

export default function ProgramInfo({
  accessibility_options,
  age_requirements,
  age_specializations,
  city_requirements,
  county_requirements,
  delivery_options,
  displayName,
  gender_requirements,
  languages,
  state_requirements,
  population_restrictions,
  population_specializations,
  postal_code_requirements,
  payment_options,
  transportation_options,
}) {
  const { formatList, formatMessage } = useIntl();

  const filteredPopulationSpecializations = useFilteredPopulations(population_specializations);
  const filteredPopulationRestrictions = useFilteredPopulations(population_restrictions);

  const formattedProps = useMemo(() => {
    const agesSpecializedText = formatAgeRange(age_specializations, formatMessage);
    const agesServedText = formatAgeRange(age_requirements, formatMessage);
    const translate = translateOptions(formatMessage);

    const accOptions = translate(accessibility_options, accOptionsMessages);
    const deliveryOptions = translate(delivery_options, deliveryOptMessages);
    const languageOptions = translate(languages, languageOptMessages);
    const populationSpecializationsOptions = translate(filteredPopulationSpecializations, populationOptMessages);
    const populationRestrictionsOptions = translate(filteredPopulationRestrictions, populationOptMessages);
    const paymentOptions = translate(payment_options, paymentOptMessages);
    const transportationOptions = translate(transportation_options, transportationOptMessages);

    return {
      agesServedText,
      agesSpecializedText,
      accessibilityServices: formatList(accOptions, { type: 'unit' }),
      cityRequirementsText: formatGeographicRestriction(city_requirements),
      countyRequirementsText: formatGeographicRestriction(county_requirements),
      deliveryOptionsText: formatList(deliveryOptions, { type: 'unit' }),
      genderRequirementsText: gender_requirements.join(', '),
      languagesSpokenText: formatList(languageOptions, { type: 'unit' }),
      populationRestrictionsText: formatList(populationRestrictionsOptions, { type: 'unit' }),
      populationSpecializationsText: formatList(populationSpecializationsOptions, { type: 'unit' }),
      postalCodeRequirementsText: postal_code_requirements.join(', '),
      stateRequirementsText: state_requirements.join(', '),
      paymentOptionsText: formatList(paymentOptions, { type: 'unit' }),
      transportationOptionsText: formatList(transportationOptions, { type: 'unit' }),

    };
  }, [
    accessibility_options,
    age_requirements,
    age_specializations,
    city_requirements,
    county_requirements,
    delivery_options,
    filteredPopulationSpecializations,
    gender_requirements,
    languages,
    state_requirements,
    filteredPopulationRestrictions,
    postal_code_requirements,
    payment_options,
    transportation_options,
    formatList,
    formatMessage,
  ]);

  const {
    agesServedText,
    agesSpecializedText,
    accessibilityServices,
    cityRequirementsText,
    countyRequirementsText,
    deliveryOptionsText,
    genderRequirementsText,
    populationRestrictionsText,
    languagesSpokenText,
    populationSpecializationsText,
    postalCodeRequirementsText,
    stateRequirementsText,
    paymentOptionsText,
    transportationOptionsText,
  } = formattedProps;

  const whoWeServe = stateRequirementsText
    || postalCodeRequirementsText
    || countyRequirementsText
    || cityRequirementsText
    || agesServedText
    || genderRequirementsText
    || populationRestrictionsText;

  const otherInfo = deliveryOptionsText
    || languagesSpokenText
    || populationSpecializationsText
    || agesSpecializedText
    || accessibilityServices
    || paymentOptionsText
    || transportationOptionsText;

  return (
    <>
      {whoWeServe && (
        <div className={`${displayName}--who-we-serve`}>
          <h3>
            <strong>
              <FormattedMessage
                id="ProgrameInfo.whoWeServe"
                defaultMessage="Who We Serve:"
              />
            </strong>
          </h3>
          <ul>
            {stateRequirementsText && <ProgramInfoItem title={formatMessage(messages.states)} options={stateRequirementsText} />}
            {postalCodeRequirementsText
              && <ProgramInfoItem title={formatMessage(messages.zipCodes)} options={postalCodeRequirementsText} />}
            {countyRequirementsText && <ProgramInfoItem title={formatMessage(messages.counties)} options={countyRequirementsText} />}
            {cityRequirementsText && <ProgramInfoItem title={formatMessage(messages.cities)} options={cityRequirementsText} />}
            {agesServedText && <ProgramInfoItem title={formatMessage(messages.ages)} options={agesServedText} />}
            {genderRequirementsText && <ProgramInfoItem title={formatMessage(messages.genders)} options={genderRequirementsText} />}
            {populationRestrictionsText?.length > 0
            && <ProgramInfoItem title={formatMessage(messages.populations)} options={populationRestrictionsText} />}
          </ul>
        </div>
      )}
      {otherInfo && (
        <div className={`${displayName}--other-info`}>
          <h3>
            <strong>
              <FormattedMessage
                id="ProgramSearch.otherInformation"
                defaultMessage="Other Important Information:"
              />
            </strong>
          </h3>
          <ul>
            {deliveryOptionsText
              && <ProgramInfoItem title={formatMessage(messages.programDelivery)} options={deliveryOptionsText} />}
            {languagesSpokenText
              && <ProgramInfoItem title={formatMessage(messages.supportedLanguages)} options={languagesSpokenText} />}
            {populationSpecializationsText?.length > 0
              && <ProgramInfoItem title={formatMessage(messages.populationsSpecialized)} options={populationSpecializationsText} />}
            {agesSpecializedText
              && <ProgramInfoItem title={formatMessage(messages.agesSpecializedIn)} options={agesSpecializedText} />}
            {accessibilityServices
              && <ProgramInfoItem title={formatMessage(messages.accessibilityServices)} options={accessibilityServices} />}
            {transportationOptionsText
              && <ProgramInfoItem title={formatMessage(messages.transportationOptions)} options={transportationOptionsText} />}
            {paymentOptionsText
              && <ProgramInfoItem title={formatMessage(messages.paymentOptions)} options={paymentOptionsText} />}
          </ul>
        </div>
      )}

    </>
  );
}

ProgramInfo.propTypes = {
  ...programPropsShape,
  displayName: PropTypes.string.isRequired,
};

ProgramInfo.defaultProps = {
  ...defaultProgramProps,
};
